import React, { useState, useEffect, useRef } from "react";
import {
  fetchDispatchModes,
  createDispatchMode,
  updateDispatchMode,
  deleteDispatchMode,
} from "../../api/inwardOutwardApi";
import { getCsrfToken } from "../../../../public/api/authApi";
import {
  GlobalButton,
  GlobalTextField,
  GlobalSelectField,
} from "../../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import { GlobalModal } from "../../../../global/GlobalModal";
import GlobalDataTable from "../../../../global/GlobalDataTable";
import GlobalBadge from "../../../../global/GlobalBadge";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import GlobalSuccessFlashModal from "../../../../global/GlobalSuccessFlashModal";

const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 2, label: "Inactive" },
];

const DispatchModeMaster = ({ onDirectoryChange }) => {
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    mode_name: "",
    description: "",
    status_id: 1,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formErrorDetails, setFormErrorDetails] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const alertRef = useRef(null);
  const formSectionRef = useRef(null);
  const [successMsg, setSuccessMsg] = useState("");

  const scrollToMasterForm = () => {
    requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  };

  useEffect(() => {
    loadModes();
  }, []);

  const loadModes = async () => {
    try {
      setLoading(true);
      const response = await fetchDispatchModes({ scope: "master" });
      if (response && response.success) {
        setModes(response.data || []);
      } else {
        setModes(response || []);
      }
    } catch (err) {
      setError(err.message || "Failed to load dispatch modes");
    } finally {
      setLoading(false);
    }
  };

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrorDetails[name]) {
      setFormErrorDetails((prev) => ({ ...prev, [name]: "" }));
    }
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setFormError(null);
    setFormErrorDetails({});
    setSuccessMsg("");
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      let result;
      if (isEditing) {
        result = await updateDispatchMode(
          formData.id,
          {
            mode_name: formData.mode_name.trim(),
            description: formData.description,
            status_id: Number(formData.status_id),
          },
          csrfToken,
        );
      } else {
        result = await createDispatchMode(
          {
            mode_name: formData.mode_name,
            description: formData.description,
            status_id: Number(formData.status_id),
          },
          csrfToken,
        );
      }
      if (result?.error) {
        setFormError(result.error.message || "Failed to save dispatch mode");
        setFormErrorDetails(result.error.details || {});
        setTimeout(() => alertRef.current?.focus(), 0);
        return;
      }
      if (result?.reactivated) {
        setSuccessMsg("Dispatch mode restored to the directory.");
      } else {
        setSuccessMsg(
          isEditing
            ? "Dispatch mode updated successfully."
            : "Dispatch mode created successfully.",
        );
      }
      handleCancel();
      await loadModes();
      onDirectoryChange?.();
    } catch (err) {
      setFormError(err.message);
      setTimeout(() => alertRef.current?.focus(), 0);
    }
  };

  const handleEdit = (mode) => {
    setSuccessMsg("");
    setFormData({
      id: mode.id,
      mode_name: mode.mode_name,
      description: mode.description || "",
      status_id:
        mode.status_id != null && mode.status_id !== ""
          ? Number(mode.status_id)
          : 1,
    });
    setIsEditing(true);
    scrollToMasterForm();
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deleteDispatchMode(itemToDelete.id, csrfToken);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      setSuccessMsg("Dispatch mode removed from the directory.");
      await loadModes();
      onDirectoryChange?.();
    } catch (err) {
      alert("Failed to remove: " + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setFormData({ id: null, mode_name: "", description: "", status_id: 1 });
    setFormError(null);
    setFormErrorDetails({});
    setIsEditing(false);
  };

  if (loading) return <p className="ub-loading">Loading Dispatch Modes…</p>;
  if (error) return <p className="ub-error">Error: {error}</p>;

  const dataList = Array.isArray(modes) ? modes : modes.data || [];
  const ALERT_ID = "ms-dispatch-mode-master-alert";

  return (
    <div className="ub-master-section">
      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.25rem",
        }}
      >
        <h3 className="ub-section__subhead" style={{ margin: 0 }}>
          Dispatch Mode Master
        </h3>
      </div>

      <div ref={formSectionRef} style={{ scrollMarginTop: "1rem" }}>
        <GlobalFormShell
          legend={isEditing ? "Edit dispatch mode" : "Add dispatch mode"}
          showAlert={!!formError}
          alertText={formError}
          alertId={ALERT_ID}
          formAlertRef={alertRef}
          actions={
            <>
              {isEditing ? (
                <GlobalButton variant="ghost" onClick={handleCancel}>
                  Cancel edit
                </GlobalButton>
              ) : null}
              <GlobalButton variant="primary" onClick={handleSubmit}>
                {isEditing ? "Save mode" : "Create mode"}
              </GlobalButton>
            </>
          }
        >
          <GlobalTextField
            id={isEditing ? "mode_name_edit" : "mode_name"}
            label="Mode Name"
            value={formData.mode_name}
            onChange={(v) => setFormField("mode_name", v)}
            error={formErrorDetails.mode_name}
            baseAlertId={ALERT_ID}
            required
          />
          <GlobalTextField
            id={isEditing ? "description_edit" : "description"}
            label="Description"
            value={formData.description}
            onChange={(v) => setFormField("description", v)}
            error={formErrorDetails.description}
            baseAlertId={ALERT_ID}
          />
          {isEditing ? (
            <GlobalSelectField
              id="dispatch_mode_status_id"
              label="Status"
              value={String(formData.status_id)}
              onChange={(v) => setFormField("status_id", Number(v))}
              options={STATUS_OPTIONS}
              error={formErrorDetails.status_id}
              baseAlertId={ALERT_ID}
            />
          ) : null}
        </GlobalFormShell>
      </div>

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Dispatch mode master table"
        headers={[
          { key: "name", label: "Mode Name" },
          { key: "desc", label: "Description" },
          { key: "status", label: "Status" },
          { key: "created", label: "Created Date" },
          {
            key: "actions",
            label: "Actions",
            className: "ub-table__col-actions",
          },
        ]}
      >
        {dataList.map((mode) => (
          <tr key={mode.id}>
            <td style={{ fontWeight: "500" }}>{mode.mode_name}</td>
            <td>{mode.description || "—"}</td>
            <td>
              <GlobalBadge active={Number(mode.status_id) === 1} />
            </td>
            <td>
              {mode.created_at
                ? new Date(mode.created_at).toLocaleDateString()
                : "—"}
            </td>
            <td className="ub-table__col-actions">
              <div className="ub-table-actions">
                <GlobalTableActionButton action="edit" onClick={() => handleEdit(mode)} />
                <GlobalTableActionButton action="delete" onClick={() => handleDeleteClick(mode)} />
              </div>
            </td>
          </tr>
        ))}
        {dataList.length === 0 && (
          <tr>
            <td
              colSpan={5}
              style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}
            >
              No Dispatch Modes Found
            </td>
          </tr>
        )}
      </GlobalDataTable>

      <GlobalModal
        open={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
        title="Confirm removal"
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <GlobalButton
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isDeleting}
            >
              Remove
            </GlobalButton>
          </div>
        }
      >
        <p>
          Remove dispatch mode <b>{itemToDelete?.mode_name}</b> from the
          directory? It will be hidden from lists (marked inactive).
        </p>
      </GlobalModal>
    </div>
  );
};

export default DispatchModeMaster;
